import React, { useRef, useState, useEffect } from 'react';
import { X, Loader, Save, Camera, CheckCircle, Clock, AlertCircle, Eye, EyeOff, Lock } from 'lucide-react';
import { auth, storage } from '../../lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import type { UserProfile } from '../../types';
import { notifyToast } from '../../lib/toastStore';
import { isWorkspaceTeamRole } from '../../lib/permissions';
import Modal from '../shared/Modal';
import PasswordSetForm from '../shared/PasswordSetForm';
import InternationalPhoneInput from '../shared/InternationalPhoneInput';
import StructuredAddressForm, { EMPTY_ADDRESS, flattenAddress, parseAddress } from '../shared/StructuredAddressForm';
import type { AddressValues } from '../shared/StructuredAddressForm';
import PrivacySettingsPanel from '../legal/PrivacySettingsPanel';

interface ProfileModalProps {
  profile: UserProfile;
  onClose: () => void;
  onProfileUpdated: (updated: UserProfile) => void;
  isAdminEditing?: boolean; // True when an admin is editing another user's profile
}

export default function ProfileModal({ profile, onClose, onProfileUpdated, isAdminEditing = false }: ProfileModalProps) {
  const [firstName, setFirstName] = React.useState(profile.firstName || '');
  const [lastName, setLastName] = React.useState(profile.lastName || '');
  const [displayName, setDisplayName] = React.useState(profile.displayName || '');
  const [phoneNumber, setPhoneNumber] = React.useState(profile.phoneNumber || '');
  const [profilePhotoUrl, setProfilePhotoUrl] = React.useState(profile.profilePhotoUrl || '');
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [addressValues, setAddressValues] = React.useState<AddressValues>(() =>
    parseAddress(profile.address, profile as any)
  );
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Change Password section states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState('');

  const diditStatus = profile.didit?.status;
  const isVerified = diditStatus === 'approved';
  // Team members are strictly read-only for administrative profile information.
  // Candidate / applicant roles stored in workspaceRole, role, or workspaceMembers are NOT internal team member roles.
  // Portal roles are NEVER team-locked — mirroring the server
  // classification (serverProfileLock.ts): the profile's own portal role
  // wins over org-membership stamps (a dual-role applicant who owns a
  // referrer org carries an 'admin' membership value, which used to lock
  // them out of their own profile behind the team-member message).
  const profileRoleValue = String(profile.role || (profile as any).primaryRole || '').toLowerCase();
  const isPortalProfile = ['applicant', 'referrer', 'employer', 'sponsor'].includes(profileRoleValue);
  const isTeamMemberSelf = !isAdminEditing && !isPortalProfile && (
    isWorkspaceTeamRole(profile.workspaceRole) ||
    isWorkspaceTeamRole(profile.role) ||
    Boolean(profile.workspaceMembers && Object.values(profile.workspaceMembers).some((r: any) => isWorkspaceTeamRole(r)))
  );
  const diditLockApplies = diditStatus === 'approved' || diditStatus === 'pending' || diditStatus === 'in_progress' || diditStatus === 'in_review';
  // §3.7: applicants edit their own CONTACT fields (phone, address, city,
  // country, postal) in place even while identity verification is in
  // flight or approved — only the legal name is frozen, because it must
  // match the verified identity. Email is read-only for everyone (shown
  // above with its verification badge).
  const isApplicantSelf = !isAdminEditing && profileRoleValue === 'applicant';
  const identityLocked = isApplicantSelf && diditLockApplies;
  // Full lock (nothing editable): team members, and non-applicant portal
  // users during verification — unchanged behaviour. Applicants never hit
  // the full lock for the Didit reason; identityLocked covers them.
  const isEditingLocked = !isAdminEditing && (isTeamMemberSelf || (!isApplicantSelf && diditLockApplies));
  const verificationBadge = () => {
    if (isVerified) {
      return { icon: CheckCircle, label: 'Verified', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
    }
    if (diditStatus === 'pending' || diditStatus === 'in_review') {
      return { icon: Clock, label: diditStatus === 'in_review' ? 'Pending Manual Review' : 'Verification Pending', color: 'bg-amber-50 text-amber-700 border-amber-200' };
    }
    if (diditStatus === 'declined' || diditStatus === 'expired') {
      return { icon: AlertCircle, label: 'Not Verified', color: 'bg-red-50 text-red-700 border-red-200' };
    }
    return { icon: AlertCircle, label: 'Not Verified', color: 'bg-slate-50 text-slate-500 border-slate-200' };
  };
  const badge = verificationBadge();

  const handlePhotoUpload = async (file: File) => {
    if (!auth.currentUser) return;
    setUploadingPhoto(true);
    try {
      // When admin is editing another user's profile, upload to that user's path
      const targetUserId = isAdminEditing ? profile.uid : auth.currentUser.uid;
      const storageRef = ref(storage, `users/${targetUserId}/profile_photo_${Date.now()}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      setProfilePhotoUrl(downloadUrl);
    } catch (err) {
      console.error('Profile photo upload failed:', err);
      notifyToast('error', 'Failed to upload photo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    if (!firstName.trim()) { notifyToast('error', 'First Name is required.'); return; }
    if (!lastName.trim()) { notifyToast('error', 'Last Name is required.'); return; }
    if (!displayName.trim()) { notifyToast('error', 'Display Name is required.'); return; }
    // D3 (§3.6/§3.7): for applicants, phone is optional in the profile —
    // it becomes required only when an application is submitted. Format
    // is still validated inside InternationalPhoneInput.
    if (!phoneNumber.trim() && !isApplicantSelf) { notifyToast('error', 'Phone Number is required.'); return; }
    setSaving(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      const flatAddr = flattenAddress(addressValues);
      const res = await fetch('/api/profile/update', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          displayName: displayName.trim(),
          phoneNumber: phoneNumber.trim(),
          address: flatAddr,
          addressLine1: addressValues.addressLine1,
          addressLine2: addressValues.addressLine2,
          city: addressValues.city,
          state: addressValues.state,
          country: addressValues.country,
          postalCode: addressValues.postalCode,
          // Profile photo URL is written via the server (Admin SDK) to avoid
          // client-side Firestore permission failures on stale tokens.
          profilePhotoUrl: profilePhotoUrl !== profile.profilePhotoUrl ? profilePhotoUrl : undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        console.error('Profile update failed:', data?.error || `HTTP ${res.status}`);
        notifyToast('error', data?.error || 'Failed to update profile. Please try again.');
        return;
      }
      if (data.profile) {
        onProfileUpdated({ ...data.profile, profilePhotoUrl } as UserProfile);
      }
      onClose();
    } catch (err: any) {
      console.error('Profile update failed:', err);
      notifyToast('error', 'Failed to update profile. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-extrabold text-slate-900">My Profile</h3>
            <p className="text-caption text-slate-400 mt-0.5">Manage your account information</p>
          </div>
          <button aria-label="Close" title="Close" onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all cursor-pointer">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Avatar */}
          <div className="flex justify-center">
            <div
              onClick={() => { if (!isEditingLocked) photoInputRef.current?.click(); }}
              className={`relative h-20 w-20 rounded-full bg-blue-100 flex items-center justify-center text-2xl font-extrabold text-brand overflow-hidden ${isEditingLocked ? '' : 'cursor-pointer group'} ring-2 ring-blue-200 ${isEditingLocked ? '' : 'hover:ring-blue-400'} transition-all`}
            >
              {profilePhotoUrl ? (
                <img src={profilePhotoUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span>
                  {displayName
                    ? displayName.charAt(0).toUpperCase()
                    : firstName
                      ? firstName.charAt(0).toUpperCase()
                      : profile.email.charAt(0).toUpperCase()}
                </span>
              )}
              {!isEditingLocked && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
                  {uploadingPhoto ? (
                    <Loader className="h-6 w-6 text-white animate-spin" />
                  ) : (
                    <Camera className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-all" />
                  )}
                </div>
              )}
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (file) handlePhotoUpload(file);
                e.target.value = '';
              }}
            />
          </div>

          <div className="space-y-1">
            <label className="text-caption font-bold text-slate-500 uppercase">Email</label>
            <div className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-xs text-slate-500 select-all flex items-center justify-between">
              <span>{profile.email}</span>
              {auth.currentUser?.emailVerified ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-[10px] font-bold shrink-0">
                  <CheckCircle className="h-3 w-3" />
                  Verified
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-[10px] font-bold shrink-0">
                  <Clock className="h-3 w-3" />
                  Unverified
                </span>
              )}
            </div>
          </div>

          {/* Identity Verification Status */}
          <div className="space-y-1">
            <label className="text-caption font-bold text-slate-500 uppercase">Identity Verification</label>
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-bold ${badge.color}`}>
              <badge.icon className="h-3.5 w-3.5" />
              {badge.label}
            </div>
          </div>

          {isEditingLocked && (
            <div className="px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 font-medium flex items-start gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {isTeamMemberSelf
                  ? 'Team Member administrative profiles are locked. Please contact a Super Admin to request updates to your profile details.'
                  : isVerified
                  ? 'Your identity is verified. Profile changes are locked. Contact your Advisor to update this information.'
                  : 'You have a pending identity verification. Profile editing is locked until verification is complete.'}
              </span>
            </div>
          )}

          {/* §3.7: applicants whose identity is verification-locked get a
              PLAIN explanation of what is locked and why — and a working
              form for everything else (phone, address). */}
          {!isEditingLocked && identityLocked && (
            <div className="px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 font-medium flex items-start gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Your legal name and email are linked to your identity verification, so they can&rsquo;t be changed here.
                If either needs updating, contact the ISATS team and they&rsquo;ll verify the change with you.
                You can update your phone number and address here at any time.
              </span>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-caption font-bold text-slate-500 uppercase">Display Name <span className="text-slate-300 font-normal">(appears everywhere)</span></label>
            <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="e.g. John Smith" required disabled={isEditingLocked} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400 transition-all disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed" />
          </div>

          <div className="space-y-1">
            <label className="text-caption font-bold text-slate-500 uppercase">First Name</label>
            <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" required disabled={isEditingLocked || identityLocked} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400 transition-all disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed" />
          </div>

          <div className="space-y-1">
            <label className="text-caption font-bold text-slate-500 uppercase">Last Name</label>
            <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" required disabled={isEditingLocked || identityLocked} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-xs outline-none focus:border-blue-400 transition-all disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed" />
          </div>

          <div className="space-y-1">
            <InternationalPhoneInput
              value={phoneNumber}
              countryCode={addressValues.country}
              onChange={(e164) => setPhoneNumber(e164)}
              label="Phone Number"
              required={!isApplicantSelf}
              disabled={isEditingLocked}
            />
          </div>

          {/* Structured Address */}
          <div className="text-left">
            <label className="text-caption font-bold text-slate-500 uppercase mb-2 block">Home Address</label>
            <StructuredAddressForm
              values={addressValues}
              onChange={setAddressValues}
              disabled={isEditingLocked}
              showIcons={false}
            />
          </div>

          {/* Change Password Section */}
          <div className="border-t border-slate-100 pt-4 text-left">
            <button
              type="button"
              onClick={() => setShowChangePassword(!showChangePassword)}
              className="text-xs font-bold text-slate-500 hover:text-slate-800 flex items-center gap-1 cursor-pointer transition uppercase"
            >
              <span>{showChangePassword ? 'Hide Change Password' : 'Change Password'}</span>
            </button>
            {showChangePassword && (
              <div className="mt-3 space-y-4 bg-slate-50 p-4 rounded-2xl border border-slate-200/60 animate-slide-down">
                {changePasswordSuccess ? (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-bold px-4 py-3 rounded-xl shadow-sm">
                    Password updated successfully!
                  </div>
                ) : (
                  <>
                    {changePasswordError && (
                      <div className="bg-red-50 border border-red-200 text-red-800 text-xs font-bold px-4 py-3 rounded-xl shadow-sm">
                        {changePasswordError}
                      </div>
                    )}
                    <div className="space-y-1">
                      <label className="text-xs font-semibold text-slate-600">Current Password</label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          placeholder="••••••••"
                          className="w-full pl-10 pr-10 py-2.5 text-xs border border-slate-200 rounded-xl focus:outline-none focus:border-blue-400 bg-white"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                          aria-pressed={showCurrentPassword}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                        >
                          {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    <PasswordSetForm
                      onSubmit={async (newPass) => {
                        if (!currentPassword) {
                          setChangePasswordError('Please enter your current password.');
                          return;
                        }
                        setChangePasswordLoading(true);
                        setChangePasswordError('');
                        try {
                          const token = await auth.currentUser?.getIdToken();
                          const res = await fetch('/api/auth/change-password', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              ...(token ? { Authorization: `Bearer ${token}` } : {}),
                            },
                            body: JSON.stringify({ currentPassword, newPassword: newPass, firebaseApiKey: import.meta.env.VITE_FIREBASE_API_KEY || '' }),
                          });
                          const data = await res.json();
                          if (!res.ok) {
                            setChangePasswordError(data.error || 'Failed to update password.');
                            return;
                          }
                          setChangePasswordSuccess(true);
                          setCurrentPassword('');
                        } catch (err: any) {
                          setChangePasswordError('Network error. Please try again.');
                        } finally {
                          setChangePasswordLoading(false);
                        }
                      }}
                      submitLabel="Update Password"
                      loading={changePasswordLoading}
                      showRecoveryEmail={false}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Wave 4 (D-04): data-rights requests live in account settings.
            Only on the user's OWN profile — the panel acts on the
            signed-in account, never on an admin-edited other user. */}
        {!isAdminEditing && (
          <div className="px-5 py-4 border-t border-slate-200">
            <PrivacySettingsPanel />
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200">
          {isEditingLocked ? (
            <button onClick={onClose} className="px-4 py-1.5 btn-primary  text-white text-caption font-bold rounded-xl transition-all cursor-pointer">
              Close
            </button>
          ) : (
            <>
              <button onClick={onClose} className="px-3 py-1.5 border border-slate-200 text-caption font-bold rounded-lg text-slate-500 hover:bg-slate-50 transition-all cursor-pointer">Cancel</button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-1.5 btn-primary  disabled:bg-slate-300 text-white text-caption font-bold rounded-xl transition-all cursor-pointer disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {saving ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save Changes
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
